import { Button, DialogContentText } from "@material-ui/core";
import ComboListPage from "@saleor/combos/components/ComboListPage";
import { useComboBulkEnableMutation } from "@saleor/combos/mutations";
import { useCombosQuery } from "@saleor/combos/queries";
import {
  comboAddUrl,
  comboListUrl,
  ComboListUrlDialog,
  ComboListUrlQueryParams,
  comboUrl
} from "@saleor/combos/urls";
import ActionDialog from "@saleor/components/ActionDialog";
import DeleteFilterTabDialog from "@saleor/components/DeleteFilterTabDialog";
import SaveFilterTabDialog, {
  SaveFilterTabDialogFormData
} from "@saleor/components/SaveFilterTabDialog";
import { configurationMenuUrl } from "@saleor/configuration";
import useBulkActions from "@saleor/hooks/useBulkActions";
import useListSettings from "@saleor/hooks/useListSettings";
import useNavigator from "@saleor/hooks/useNavigator";
import useNotifier from "@saleor/hooks/useNotifier";
import usePaginator, {
  createPaginationState
} from "@saleor/hooks/usePaginator";
import useShop from "@saleor/hooks/useShop";
import { commonMessages } from "@saleor/intl";
import { maybe } from "@saleor/misc";
import { ListViews } from "@saleor/types";
import createDialogActionHandlers from "@saleor/utils/handlers/dialogActionHandlers";
import createFilterHandlers from "@saleor/utils/handlers/filterHandlers";
import createSortHandler from "@saleor/utils/handlers/sortHandler";
import { getSortParams } from "@saleor/utils/sort";
import React from "react";
import { FormattedMessage, useIntl } from "react-intl";

import {
  areFiltersApplied,
  deleteFilterTab,
  getActiveFilters,
  getFilterOpts,
  getFilterQueryParam,
  getFilterTabs,
  getFilterVariables,
  saveFilterTab
} from "./filter";
import { getSortQueryVariables } from "./sort";

interface ComboListProps {
  params: ComboListUrlQueryParams;
}

const ComboList: React.FC<ComboListProps> = ({ params }) => {
  const { updateListSettings, settings } = useListSettings(
    ListViews.COMBO_LIST
  );
  const notify = useNotifier();
  const intl = useIntl();
  const tabs = getFilterTabs();
  const navigate = useNavigator();
  const paginate = usePaginator();
  const { isSelected, listElements, toggle, toggleAll, reset } = useBulkActions(
    params.ids
  );

  const paginationState = createPaginationState(settings?.rowNumber, params);
  const queryVariables = React.useMemo(
    () => ({
      ...paginationState,
      filter: getFilterVariables(params),
      sort: getSortQueryVariables(params)
    }),
    [params]
  );
  const { data, loading, refetch } = useCombosQuery({
    variables: queryVariables
  });

  const [openModal, closeModal] = createDialogActionHandlers<
    ComboListUrlDialog,
    ComboListUrlQueryParams
  >(navigate, comboListUrl, params);

  const handleTabChange = (tab: number) => {
    reset();
    navigate(
      comboListUrl({
        activeTab: tab.toString(),
        ...getFilterTabs()[tab - 1].data
      })
    );
  };
  const currentTab =
    params.activeTab === undefined
      ? areFiltersApplied(params)
        ? tabs.length + 1
        : 0
      : parseInt(params.activeTab, 0);

  const { loadNextPage, loadPreviousPage, pageInfo } = paginate(
    maybe(() => data.combos.pageInfo),
    paginationState,
    params
  );
  const handleSort = createSortHandler(navigate, comboListUrl, params);
  const handleTabSave = (data: SaveFilterTabDialogFormData) => {
    saveFilterTab(data.name, getActiveFilters(params));
    handleTabChange(tabs.length + 1);
  };
  const handleTabDelete = () => {
    deleteFilterTab(currentTab);
    navigate(comboListUrl());
  };
  const [comboBulkEnable, comboBulkEnableOpts] = useComboBulkEnableMutation({
    onCompleted: data => {
      if (data.comboBulkEnable.errors.length === 0) {
        notify({
          status: "success",
          text: intl.formatMessage(commonMessages.savedChanges)
        });
        refetch();
        closeModal();
      }
    }
  });
  const shop = useShop();
  const currencySymbol = maybe(() => shop.defaultCurrency, "USD");
  const [
    changeFilters,
    resetFilters,
    handleSearchChange
  ] = createFilterHandlers({
    cleanupFn: reset,
    createUrl: comboListUrl,
    getFilterQueryParam,
    navigate,
    params
  });
  return (
    <>
      <ComboListPage
        combos={maybe(() => data.combos.edges.map(edge => edge.node), [])}
        toatlCount={maybe(() => data.combos.totalCount, 0)}
        currentTab={currentTab}
        initialSearch={params.query || ""}
        onSearchChange={handleSearchChange}
        onAll={resetFilters}
        onTabChange={handleTabChange}
        onTabDelete={() => openModal("delete-search")}
        onTabSave={() => openModal("save-search")}
        tabs={tabs.map(tab => tab.name)}
        settings={settings}
        onSort={handleSort}
        sort={getSortParams(params)}
        onAdd={() => navigate(comboAddUrl())}
        onRowClick={id => () => navigate(comboUrl(id))}
        disabled={loading}
        onNextPage={loadNextPage}
        onPreviousPage={loadPreviousPage}
        onUpdateListSettings={updateListSettings}
        pageInfo={pageInfo}
        isChecked={isSelected}
        selected={listElements.length}
        toggle={toggle}
        toggleAll={toggleAll}
        onBack={() => navigate(configurationMenuUrl)}
        toolbar={
          <>
            <Button
              color="primary"
              onClick={() =>
                openModal("unenable", {
                  ids: listElements
                })
              }
            >
              <FormattedMessage
                defaultMessage="UnEnable"
                description="unenable combo"
              />
            </Button>
            <Button
              color="primary"
              onClick={() =>
                openModal("enable", {
                  ids: listElements
                })
              }
            >
              <FormattedMessage
                defaultMessage="Enable"
                description="enable combo"
              />
            </Button>
          </>
        }
        filterOpts={getFilterOpts(params)}
        currencySymbol={currencySymbol}
        onFilterChange={changeFilters}
      />
      <ActionDialog
        open={
          params.action === "unenable" && maybe(() => params.ids.length > 0)
        }
        onClose={closeModal}
        confirmButtonState={comboBulkEnableOpts.status}
        onConfirm={() =>
          comboBulkEnable({
            variables: {
              ids: params.ids,
              enable: false
            }
          })
        }
        variant="default"
        title={intl.formatMessage({
          defaultMessage: "UnEnable Combo",
          description: "dialog title"
        })}
      >
        <DialogContentText>
          <FormattedMessage
            defaultMessage="{counter,plural,one{Are you sure you want to UnEnable this Combo?} other{Are you sure you want to UnEnable {displayQuantity} Combos?}}"
            values={{
              counter: maybe(() => params.ids.length),
              displayQuantity: <strong>{maybe(() => params.ids.length)}</strong>
            }}
          />
        </DialogContentText>
      </ActionDialog>
      <ActionDialog
        open={params.action === "enable" && maybe(() => params.ids.length > 0)}
        onClose={closeModal}
        confirmButtonState={comboBulkEnableOpts.status}
        onConfirm={() =>
          comboBulkEnable({
            variables: {
              ids: params.ids,
              enable: true
            }
          })
        }
        variant="default"
        title={intl.formatMessage({
          defaultMessage: "Enable Combo",
          description: "dialog title"
        })}
      >
        <DialogContentText>
          <FormattedMessage
            defaultMessage="{counter,plural,one{Are you sure you want to Enable this Combo?} other{Are you sure you want to Enable {displayQuantity} Combos?}}"
            values={{
              counter: maybe(() => params.ids.length),
              displayQuantity: <strong>{maybe(() => params.ids.length)}</strong>
            }}
          />
        </DialogContentText>
      </ActionDialog>
      <SaveFilterTabDialog
        open={params.action === "save-search"}
        confirmButtonState="default"
        onClose={closeModal}
        onSubmit={handleTabSave}
      />
      <DeleteFilterTabDialog
        open={params.action === "delete-search"}
        confirmButtonState="default"
        onClose={closeModal}
        onSubmit={handleTabDelete}
        tabName={maybe(() => tabs[currentTab - 1].name, "...")}
      />
    </>
  );
};
export default ComboList;
